import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, CartesianGrid, Cell
} from 'recharts';

const Dashboard = ({ balance, summary, transactions, monthlySummary, quarterlySummary, formatCurrency, formatDate, partners }) => {
  const [periodType, setPeriodType] = React.useState('monthly'); // 'monthly' or 'quarterly'

  const activeSummary = periodType === 'monthly' ? monthlySummary : quarterlySummary;

  const formatCompactNumber = (number) => {
    if (number === 0) return '0';
    return Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(number);
  };

  const stats = [
    {
      label: 'Total Income',
      value: summary.totalIncome || 0,
      icon: '💰',
      type: 'success',
      description: 'Project payments'
    },
    {
      label: 'Total Expenses',
      value: summary.totalExpenses || 0,
      icon: '💸',
      type: 'danger',
      description: 'Business costs'
    },
    {
      label: 'Net Profit',
      value: (summary.totalIncome || 0) - (summary.totalExpenses || 0),
      icon: '📊',
      type: (summary.totalIncome || 0) - (summary.totalExpenses || 0) >= 0 ? 'success' : 'danger',
      description: 'Income - Expenses'
    },
    {
      label: 'Total Loans',
      value: summary.totalLoans || 0,
      icon: '💳',
      type: 'warning',
      description: 'Founder loans'
    }
  ];

  // Calculate growth metrics based on active period
  const growthData = activeSummary.map((m, index) => {
    const prev = index > 0 ? activeSummary[index - 1] : null;
    const incomeGrowth = prev && prev.income > 0 ? ((m.income - prev.income) / prev.income) * 100 : 0;
    const expenseGrowth = prev && prev.expenses > 0 ? ((m.expenses - prev.expenses) / prev.expenses) * 100 : 0;
    const savingsRate = m.income > 0 ? ((m.income - m.expenses) / m.income) * 100 : 0;

    return {
      ...m,
      displayLabel: periodType === 'monthly' ? new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short' }) : m.label,
      shortMonth: periodType === 'monthly' ? new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short' }) : m.label,
      incomeGrowth,
      expenseGrowth,
      savingsRate,
      profit: m.income - m.expenses
    };
  });

  const CustomTooltip = ({ active, payload, label, isPercentage = false }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'var(--primary-bg)',
          border: '1px solid var(--border)',
          padding: '10px',
          borderRadius: '6px'
        }}>
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {isPercentage ? `${entry.value.toFixed(1)}%` : formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-6 flex-mobile-col">
        <h2 className="text-2xl font-bold text-yellow">📊 Dashboard Overview</h2>
        <div className="flex items-center gap-4">
          <div className="flex bg-secondary p-1 rounded-lg">
            <button
              onClick={() => setPeriodType('monthly')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${periodType === 'monthly' ? 'bg-primary text-yellow shadow-lg' : 'text-muted hover:text-white'}`}
            >
              MONTHLY
            </button>
            <button
              onClick={() => setPeriodType('quarterly')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${periodType === 'quarterly' ? 'bg-primary text-yellow shadow-lg' : 'text-muted hover:text-white'}`}
            >
              QUARTERLY
            </button>
          </div>
          <div className="text-right flex-mobile-hide">
            <p className="text-xs text-muted">Current Balance</p>
            <p className="text-xl font-bold text-blue">{formatCurrency(balance)}</p>
          </div>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 mb-6">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.type} slide-up`} style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="flex items-center justify-center mb-3">
              <span style={{ fontSize: '2rem' }}>{stat.icon}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray mb-1">{stat.label}</h3>
            <p className={`stat-value ${stat.type === 'success' ? 'text-green' : stat.type === 'danger' ? 'text-red' : 'text-orange'}`}>
              {formatCurrency(stat.value)}
            </p>
            <p className="text-xs text-muted">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Growth Visualization */}
      <div className="card mb-6">
        <h3 className="text-xl font-semibold mb-4 text-yellow">📈 {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} Growth Trend</h3>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={growthData}>
              <defs>
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="displayLabel" stroke="var(--text-primary)" />
              <YAxis stroke="var(--text-primary)" tickFormatter={formatCompactNumber} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="income" name="Income" stroke="#22c55e" fillOpacity={1} fill="url(#colorIncome)" />
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" fillOpacity={1} fill="url(#colorExpenses)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Profitability Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 text-yellow">💰 {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} Profitability</h3>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="displayLabel" stroke="var(--text-primary)" />
                <YAxis stroke="var(--text-primary)" tickFormatter={formatCompactNumber} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="profit" name="Net Profit">
                  {growthData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#22c55e' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Unique Graph: Savings Rate Trend */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 text-yellow">🎯 {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} Savings Rate (%)</h3>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <AreaChart data={growthData}>
                <defs>
                  <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="displayLabel" stroke="var(--text-primary)" />
                <YAxis stroke="var(--text-primary)" unit="%" />
                <Tooltip content={<CustomTooltip isPercentage={true} />} />
                <Area type="monotone" dataKey="savingsRate" name="Savings Rate" stroke="#3b82f6" fillOpacity={1} fill="url(#colorSavings)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Growth Metrics */}
      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4 text-yellow">🚀 Recent Performance ({periodType === 'monthly' ? 'Last 3 Months' : 'Last 3 Quarters'})</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {growthData.slice(-3).reverse().map((data, index) => (
            <div key={index} className="p-4" style={{ background: 'var(--secondary-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
              <div className="flex justify-between items-center mb-3">
                <span className="font-bold text-lg">{periodType === 'monthly' ? data.month : data.label}</span>
                <span className={`px-2 py-1 rounded text-xs font-bold ${data.profit >= 0 ? 'bg-green-100 text-green' : 'bg-red-100 text-red'}`} style={{ background: data.profit >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)' }}>
                  {data.profit >= 0 ? 'PROFIT' : 'LOSS'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted">Net Profit</span>
                  <span className={`font-bold ${data.profit >= 0 ? 'text-green' : 'text-red'}`}>{formatCurrency(data.profit)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted">Savings Rate</span>
                  <span className="font-bold text-blue">{data.savingsRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-xs text-muted">Income Growth</span>
                  <span className={data.incomeGrowth >= 0 ? 'text-green' : 'text-red'}>
                    {data.incomeGrowth >= 0 ? '↑' : '↓'} {Math.abs(data.incomeGrowth).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;